import React, { useState, useEffect, useMemo } from 'react';

import { Checkbox } from '../../../components/ui/Checkbox';
import { getTranslation } from '../../../utils/translations';
import userRoleService from '../../../services/userRoleService';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { toast } from 'sonner';
import {
  getPresetPermissionCodesForRole,
  groupPermissionsForMatrix,
  isPermissionConfigurable,
} from '../../../lib/accessControlRegistry';

const PermissionsMatrix = ({ roles, permissions, language }) => {
  const tRoot = getTranslation(language);
  const ai = tRoot?.adminIdentity || {};
  const isFr = String(language).toUpperCase() === 'FR';
  const [rolePermissions, setRolePermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [applyingRoleId, setApplyingRoleId] = useState(null);
  const [permissionSearch, setPermissionSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [savingCell, setSavingCell] = useState(null);

  useEffect(() => {
    fetchRolePermissions();
  }, [roles]);

  const permissionGroups = useMemo(
    () => groupPermissionsForMatrix(permissions, language),
    [permissions, language],
  );

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = { ...prev };
      for (const g of permissionGroups) {
        if (next[g.id] === undefined) {
          next[g.id] = true;
        }
      }
      return next;
    });
  }, [permissionGroups]);

  const fetchRolePermissions = async () => {
    try {
      setLoading(true);
      const permissionsMap = {};

      for (const role of roles || []) {
        const perms = await userRoleService?.getRolePermissions(role?.id);
        permissionsMap[role?.id] = perms?.map((p) => p?.permissionId);
      }

      setRolePermissions(permissionsMap);
    } catch (error) {
      console.error('Error fetching role permissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const setRolePermissionLocal = (roleId, permissionId, enabled) => {
    setRolePermissions((prev) => {
      const current = new Set(prev[roleId] || []);
      if (enabled) current.add(permissionId);
      else current.delete(permissionId);
      return { ...prev, [roleId]: [...current] };
    });
  };

  const handlePermissionToggle = async (role, permission, isChecked) => {
    if (role?.roleType === 'administrator') return;
    const code = permission?.permissionCode;
    if (!isPermissionConfigurable(code) || permission?.isConfigurable === false) {
      toast.message(
        isFr
          ? 'Cette permission est en lecture seule (legacy ou non utilisée par l’app).'
          : 'This permission is read-only (legacy or unused by the app).',
      );
      return;
    }

    const cellKey = `${role?.id}:${permission?.id}`;
    setSavingCell(cellKey);
    const previousHad = hasPermission(role?.id, permission?.id);
    setRolePermissionLocal(role?.id, permission?.id, isChecked);

    try {
      if (isChecked) {
        await userRoleService?.assignPermissionToRole(role?.id, permission?.id);
      } else {
        await userRoleService?.removePermissionFromRole(role?.id, permission?.id);
      }
      await fetchRolePermissions();
      toast.success(isFr ? 'Permission enregistrée' : 'Permission saved');
    } catch (error) {
      console.error('Error toggling permission:', error);
      setRolePermissionLocal(role?.id, permission?.id, previousHad);
      toast.error(isFr ? 'Impossible de mettre à jour' : 'Update failed', {
        description: error?.message || String(error),
      });
    } finally {
      setSavingCell(null);
    }
  };

  const handleApplyPreset = async (role) => {
    if (!role?.id) return;
    setApplyingRoleId(role.id);
    try {
      const preset = getPresetPermissionCodesForRole(role.roleType);
      const result = await userRoleService.applyRoleCapabilityPreset(
        role.id,
        role.roleType,
        preset,
      );
      await fetchRolePermissions();
      toast.success(
        isFr
          ? `Profil appliqué : ${result.added} ajout(s), ${result.skipped} déjà présent(s).`
          : `Preset applied: ${result.added} added, ${result.skipped} already set.`,
      );
    } catch (error) {
      toast.error(isFr ? 'Échec du profil recommandé' : 'Preset failed', {
        description: error?.message || String(error),
      });
    } finally {
      setApplyingRoleId(null);
    }
  };

  const hasPermission = (roleId, permissionId) =>
    rolePermissions?.[roleId]?.includes(permissionId) || false;

  const isRowConfigurable = (permission) => {
    if (permission?.isConfigurable === false) return false;
    return isPermissionConfigurable(permission?.permissionCode);
  };

  const filteredGroups = useMemo(() => {
    const q = permissionSearch.trim().toLowerCase();
    return permissionGroups
      .map((group) => {
        const matchesQuery = (p) => {
          if (!q) return true;
          const code = (p?.permissionCode || '').toLowerCase();
          const name = (p?.permissionName || '').toLowerCase();
          const desc = (p?.description || '').toLowerCase();
          return code.includes(q) || name.includes(q) || desc.includes(q);
        };

        if (group.displayRows?.length) {
          const filteredDisplayRows = group.displayRows.filter((row) => {
            if (row.kind === 'section') return !q;
            return matchesQuery(row.permission);
          });
          return {
            ...group,
            displayRows: filteredDisplayRows,
            permissions: filteredDisplayRows
              .filter((r) => r.kind === 'permission')
              .map((r) => r.permission),
          };
        }

        const perms = (group.permissions || []).filter(matchesQuery);
        return { ...group, permissions: perms };
      })
      .filter((g) => g.permissions.length > 0);
  }, [permissionGroups, permissionSearch]);

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev?.[groupId],
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">
          {isFr ? 'Matrice des permissions' : 'Permissions matrix'}
        </h2>
        <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
          {isFr
            ? 'Pages visibles dans le menu (complément du rôle utilisateur). Les actions sur les dossiers et les jalons de workflow ne se configurent pas ici.'
            : 'Pages visible in the menu (in addition to the user role). Case actions and workflow milestones are not configured here.'}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4 max-w-3xl">
        <div className="flex-1 min-w-[200px]">
          <Input
            type="search"
            label={isFr ? 'Recherche' : 'Search'}
            value={permissionSearch}
            onChange={(e) => setPermissionSearch(e?.target?.value)}
            placeholder={ai.permissionsSearchPlaceholder}
          />
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-foreground min-w-[14rem]">
                  {isFr ? 'Capacité' : 'Capability'}
                </th>
                {roles?.map((role) => (
                  <th
                    key={role?.id}
                    className="px-2 py-3 text-center text-xs font-semibold text-foreground min-w-[8rem] max-w-[11rem]"
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span className="line-clamp-2" title={role?.roleName}>
                        {role?.roleName}
                      </span>
                      {role?.roleType && (
                        <span
                          className="text-[10px] font-normal text-muted-foreground truncate max-w-full"
                          title={role?.roleType}
                        >
                          {role?.roleType}
                        </span>
                      )}
                      {role?.roleType !== 'administrator' && (
                        <Button
                          type="button"
                          variant="outline"
                          size="xs"
                          className="mt-1 text-[10px] h-7 px-2"
                          disabled={applyingRoleId === role.id}
                          onClick={() => handleApplyPreset(role)}
                        >
                          {applyingRoleId === role.id
                            ? '…'
                            : isFr
                              ? 'Profil défaut'
                              : 'Default preset'}
                        </Button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredGroups?.map((group) => {
                const isOpen = expandedGroups?.[group.id] !== false;

                return (
                  <React.Fragment key={group.id}>
                    <tr className="bg-muted/30">
                      <td colSpan={(roles?.length || 0) + 1} className="px-2 py-1">
                        <button
                          type="button"
                          onClick={() => toggleGroup(group.id)}
                          className="flex w-full items-center gap-2 px-2 py-2 text-left text-sm font-semibold text-foreground hover:bg-muted/60 rounded-md"
                        >
                          <Icon
                            name={isOpen ? 'ChevronDown' : 'ChevronRight'}
                            size={18}
                            className="text-muted-foreground shrink-0"
                          />
                          <span>{group.label}</span>
                          <span className="text-xs font-normal text-muted-foreground">
                            ({group.permissions?.length})
                          </span>
                        </button>
                      </td>
                    </tr>
                    {isOpen &&
                      (group.displayRows?.length
                        ? group.displayRows
                        : group.permissions?.map((permission) => ({
                            kind: 'permission',
                            permission,
                          }))
                      ).map((row, rowIdx) => {
                        if (row.kind === 'section') {
                          return (
                            <tr
                              key={`section-${group.id}-${rowIdx}`}
                              className="border-t border-border bg-muted/10"
                            >
                              <td
                                colSpan={(roles?.length || 0) + 1}
                                className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                              >
                                {row.label}
                              </td>
                            </tr>
                          );
                        }

                        const permission = row.permission;
                        const configurable = isRowConfigurable(permission);
                        const indent = permission?.matrixIndent ?? 0;

                        return (
                          <tr
                            key={permission?.id}
                            className="border-t border-border hover:bg-muted/20"
                          >
                            <td className="px-4 py-3" style={{ paddingLeft: `${16 + indent * 16}px` }}>
                              <div>
                                <div className="font-medium text-foreground text-sm">
                                  {permission?.permissionName}
                                </div>
                                <div className="text-[11px] font-mono text-muted-foreground mt-0.5">
                                  {permission?.permissionCode}
                                </div>
                                {permission?.description && (
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {permission?.description}
                                  </div>
                                )}
                              </div>
                            </td>
                            {roles?.map((role) => {
                              const isAdmin = role?.roleType === 'administrator';
                              const checked =
                                isAdmin || hasPermission(role?.id, permission?.id);
                              const disabled =
                                isAdmin || !configurable || savingCell === `${role?.id}:${permission?.id}`;

                              return (
                                <td key={role?.id} className="px-4 py-3 text-center">
                                  <div className="flex items-center justify-center">
                                    <Checkbox
                                      checked={checked}
                                      disabled={disabled}
                                      onCheckedChange={(c) => {
                                        if (c === checked) return;
                                        void handlePermissionToggle(role, permission, c);
                                      }}
                                    />
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PermissionsMatrix;
